/** Build rows for the Progress Report (MASTER-style deal list). */

import {
  computedLatestTrustDepositIso,
  resolveProgressDateRaw,
} from "./tradeDashboardDates";

/** Columns edited with a dropdown in the progress report modal. */
export const PROGRESS_REPORT_SELECT_OPTIONS = {
  status: [
    { value: "AVAILABLE", label: "Available" },
    { value: "SOLD", label: "Sold" },
    { value: "CLOSED", label: "Closed" },
    { value: "LEASED", label: "Leased" },
    { value: "EXPIRED", label: "Expired" },
    { value: "PENDING", label: "Pending" },
    { value: "FALLEN THRU", label: "Fallen Thru" },
    { value: "DUPLICATE", label: "Duplicate" },
  ],
  type: [
    { value: "", label: "—" },
    { value: "Commercial", label: "Commercial" },
    { value: "Residential", label: "Residential" },
    { value: "Sale", label: "Sale" },
    { value: "Lease", label: "Lease" },
  ],
  tradeCategory: [
    { value: "Pre-Construction", label: "Pre-Construction" },
    { value: "Resale", label: "Resale" },
    { value: "Lease", label: "Lease" },
  ],
  compliance: [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
    { value: "Pending", label: "Pending" },
  ],
};

/** Dropdowns that stay blank until the user picks a value in Progress Report. */
export const PROGRESS_REPORT_BLANK_SELECT_KEYS = new Set([
  "tradeCategory",
  "compliance",
]);

/** One canonical stored value per status (avoids duplicate dropdown entries). */
export function canonicalTradeStatus(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "AVAILABLE";
  const upper = raw.toUpperCase();
  for (const opt of PROGRESS_REPORT_SELECT_OPTIONS.status) {
    if (opt.value === upper || opt.value === raw) return opt.value;
    if (opt.label.toUpperCase() === upper) return opt.value;
  }
  return upper;
}

function mergeProgressTracker(trade) {
  const pt = trade?.progressTracker || {};
  const k = trade?.keyInfo || {};
  return {
    ...pt,
    paymentReceivedDate:
      pt.paymentReceivedDate || k.paymentReceivedDate || "",
    paymentConfirmationSentDate:
      pt.paymentConfirmationSentDate || k.paymentConfirmationSentDate || "",
    closedTRSDate: pt.closedTRSDate || k.closedTRSDate || "",
    fileCompleteDate: pt.fileCompleteDate || k.fileCompleteDate || "",
  };
}

export function getTradePartNumber(trade) {
  const tn = trade?.tradeNumber ?? trade?.keyInfo?.tradeNumber ?? "";
  return String(tn).trim();
}

/** Base trade # without -A / -B suffix (e.g. 200-A → 200). */
export function getBaseTradeNumber(trade) {
  const part = getTradePartNumber(trade);
  const m = part.match(/^(\d+)/);
  return m ? m[1] : part;
}

function formatReportDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const parts = raw.split("-");
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m, d).toLocaleDateString("en-CA");
    }
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-CA");
  }
  return raw;
}

function parseMoneyNumber(value) {
  const n = parseFloat(String(value || "").replace(/[^0-9.-]/g, ""));
  return Number.isNaN(n) ? null : n;
}

function formatPrice(value) {
  const n = parseMoneyNumber(value);
  if (n == null || n === 0) return "";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatMoneyAmount(value) {
  const n = parseMoneyNumber(value);
  if (n == null) return "";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatTradeClassification(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getSaleClosingAR(trade) {
  const rows = trade?.commission?.saleClosingRows;
  if (!Array.isArray(rows) || !rows.length) return "";
  const ar = rows[0]?.ar;
  return ar != null && String(ar).trim() !== "" ? formatMoneyAmount(ar) : "";
}

function getOutsideBrokerNames(trade) {
  const rows = trade?.commission?.outsideBrokersRows;
  if (!Array.isArray(rows) || !rows.length) return "";
  const names = [];
  for (const row of rows) {
    const name = String(row?.brokerage || row?.agentName || "").trim();
    if (name && !names.includes(name)) names.push(name);
  }
  return names.join(", ");
}

/** Sum of Total column in Outside Brokers & Expenses. */
const TRADE_CATEGORY_VALUES = new Set(
  PROGRESS_REPORT_SELECT_OPTIONS.tradeCategory.map((o) => o.value),
);

/** Report-only category; blank until user selects and saves in Progress Report. */
function resolveTradeCategory(trade) {
  const pt = trade?.progressTracker || {};
  const v = String(pt.progressReportTradeCategory ?? "").trim();
  return TRADE_CATEGORY_VALUES.has(v) ? v : "";
}

/** Report-only compliance; blank until user selects and saves in Progress Report. */
function resolveReportCompliance(trade) {
  const pt = trade?.progressTracker || {};
  const v = String(pt.progressReportCompliance ?? "").trim();
  if (v === "Yes" || v === "No" || v === "Pending") return v;
  return "";
}

function formatAddress(k) {
  const parts = [k.streetNumber, k.streetName, k.unit]
    .map((s) => String(s ?? "").trim())
    .filter(Boolean);
  return parts.length ? parts.join(" ") : "";
}

function getClosingDateDisplay(trade) {
  const k = trade?.keyInfo || {};
  const sc = trade?.commission?.saleClosingRows?.[0];
  const raw = k.closeDate || sc?.closingDate || "";
  return raw ? formatReportDate(raw) : "";
}

function getOutsideBrokersInvoiceTotal(trade) {
  const rows = trade?.commission?.outsideBrokersRows;
  if (!Array.isArray(rows) || !rows.length) return "";
  let sum = 0;
  let hasValue = false;
  for (const row of rows) {
    const n = parseMoneyNumber(row?.total);
    if (n != null) {
      sum += n;
      hasValue = true;
    }
  }
  return hasValue ? formatMoneyAmount(sum) : "";
}

function getAgentNames(trade) {
  const names = [];
  for (const a of trade?.agentCommissionList || []) {
    const cls = (a.classification || "").toUpperCase();
    if (cls === "REFERRAL") continue;
    const name =
      a.agentName ||
      [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
    if (name && !names.includes(name)) names.push(name);
  }
  return names.join(", ");
}

export function hasProgressTrackerInvoiceYes(pt) {
  if (!pt) return false;
  return (
    pt.commissionInvoiceReceived === "Yes" ||
    pt.commissionInvoiceSentListing === "Yes" ||
    pt.commissionInvoiceSentCoop === "Yes"
  );
}

export function formatInvoicedStatus(trade) {
  const pt = mergeProgressTracker(trade);
  if (pt.invoicedDate) {
    return formatReportDate(pt.invoicedDate);
  }
  if (pt.commissionInvoiceReceived === "Requested") {
    return "Requested";
  }
  if (hasProgressTrackerInvoiceYes(pt)) {
    const raw = pt.fileCompleteDate || "";
    return raw ? formatReportDate(raw) : "";
  }
  return "";
}

function paymentReceivedDisplayDate(trade) {
  const eftIso = String(trade?.paymentReceivedEftDateIso || "").trim();
  if (eftIso) return formatReportDate(eftIso);
  const manual = resolveProgressDateRaw(trade, "paymentReceivedDate");
  if (manual) return formatReportDate(manual);
  const trustIso = computedLatestTrustDepositIso(trade);
  if (trustIso) return formatReportDate(trustIso);
  return "";
}

function paymentToAgentsDisplayDate(trade) {
  const eftIso = String(trade?.paymentToAgentsEftDateIso || "").trim();
  if (eftIso) return formatReportDate(eftIso);
  return "";
}

function formatOpenTRSDate(trade) {
  const pt = mergeProgressTracker(trade);
  if (pt.openTRSSent !== "Yes" && !pt.openTRSDate) return "";
  const raw = pt.openTRSDate || "";
  return raw ? formatReportDate(raw) : "";
}

function formatClosedTRSDate(trade) {
  const pt = mergeProgressTracker(trade);
  const raw = pt.closedTRSDate || "";
  return raw ? formatReportDate(raw) : "";
}

export function progressReportTradeLabel(trade) {
  const tn = trade?.tradeNumber ?? trade?.keyInfo?.tradeNumber ?? "";
  const sn = trade?.keyInfo?.streetNumber || "";
  const st = trade?.keyInfo?.streetName || "";
  const addr = [sn, st].filter(Boolean).join(" ");
  return addr ? `#${tn} — ${addr}` : `#${tn}`;
}

/** Searchable trade filter options (descending trade #), same labels as Progress Tracker. */
export function buildProgressReportTradeFilterOptions(trades) {
  return (trades || [])
    .map((t) => {
      const value = String(
        t.tradeNumber ?? t.keyInfo?.tradeNumber ?? "",
      ).trim();
      if (!value) return null;
      return { value, label: progressReportTradeLabel(t) };
    })
    .filter(Boolean)
    .sort((a, b) => compareBaseTradeNumberDesc(a.value, b.value));
}

export function buildProgressReportRow(trade) {
  const k = trade?.keyInfo || {};
  const pt = mergeProgressTracker(trade);
  const tradeNumber = getTradePartNumber(trade);
  return {
    rowKey: String(trade._id || tradeNumber),
    tradeId: trade._id,
    tradeNumber,
    baseTradeNumber: getBaseTradeNumber(trade),
    tradeCategory: resolveTradeCategory(trade),
    address: formatAddress(k) || "—",
    closingDate: getClosingDateDisplay(trade),
    status: canonicalTradeStatus(k.status),
    tradeClass: formatTradeClassification(k.classification),
    agent: getAgentNames(trade) || "—",
    compliance: resolveReportCompliance(trade),
    invoiceDate: formatInvoicedStatus(trade),
    openTRSDate: formatOpenTRSDate(trade),
    closedTRSDate: formatClosedTRSDate(trade),
    price: formatPrice(k.sellPrice),
    priceRaw: parseMoneyNumber(k.sellPrice) || 0,
    arAmount: getSaleClosingAR(trade),
    invoiceAmount: getOutsideBrokersInvoiceTotal(trade),
    outsideBroker: getOutsideBrokerNames(trade) || "—",
    commissionReceived: paymentReceivedDisplayDate(trade),
    paymentToAgents: paymentToAgentsDisplayDate(trade),
    followUpDate: formatReportDate(pt.followUpDate),
    action: pt.action != null ? String(pt.action).trim() : "",
    notes: pt.notes != null ? String(pt.notes).trim() : "",
  };
}

export function buildProgressReportRows(trades) {
  return (trades || [])
    .map((trade) => buildProgressReportRow(trade))
    .sort((a, b) => {
      const na = parseInt(String(a.baseTradeNumber).replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(String(b.baseTradeNumber).replace(/\D/g, ""), 10) || 0;
      if (nb !== na) return nb - na;
      return String(a.baseTradeNumber).localeCompare(String(b.baseTradeNumber));
    });
}

export function cloneProgressReportRows(rows) {
  return (rows || []).map((row) => ({ ...row }));
}

/** Merge preset options with any values already present in report rows. */
/** Options for blank-until-selected report dropdowns (no placeholder row). */
export function getProgressReportBlankSelectOptions(columnKey, currentValue) {
  const base = PROGRESS_REPORT_SELECT_OPTIONS[columnKey];
  if (!base) return [];
  const opts = base.filter((o) => o.value !== "");
  const v = String(currentValue ?? "").trim();
  if (v && !opts.some((o) => o.value === v)) {
    opts.push({ value: v, label: v });
  }
  return opts;
}

export function getProgressReportSelectOptions(columnKey, rows) {
  const base = PROGRESS_REPORT_SELECT_OPTIONS[columnKey];
  if (!base) return null;
  const labelByValue = new Map(base.map((o) => [o.value, o.label]));
  const seen = new Set();
  const merged = [];

  const add = (rawValue) => {
    const v = String(rawValue ?? "").trim();
    if (!v) return;
    const value =
      columnKey === "status" ? canonicalTradeStatus(v) : v;
    if (seen.has(value)) return;
    seen.add(value);
    merged.push({
      value,
      label: labelByValue.get(value) || v,
    });
  };

  for (const o of base) add(o.value);
  for (const row of rows || []) {
    add(row[columnKey]);
  }
  return merged;
}

export function isProgressReportSelectColumn(columnKey) {
  return Boolean(PROGRESS_REPORT_SELECT_OPTIONS[columnKey]);
}

export const PROGRESS_REPORT_COLUMNS = [
  { key: "tradeCategory", label: "Trade Category", edit: "select" },
  { key: "tradeNumber", label: "Trade #" },
  { key: "address", label: "Address" },
  { key: "closingDate", label: "Closing Date" },
  { key: "status", label: "Status" },
  { key: "tradeClass", label: "Trade Class" },
  { key: "agent", label: "Agent" },
  { key: "compliance", label: "Compliance", edit: "select" },
  { key: "invoiceDate", label: "Invoice Date", edit: "date" },
  { key: "openTRSDate", label: "Open TRS Date" },
  { key: "closedTRSDate", label: "Closed TRS Date" },
  { key: "price", label: "Price" },
  { key: "arAmount", label: "AR Amount" },
  { key: "invoiceAmount", label: "Invoice Amount" },
  { key: "outsideBroker", label: "Outside Broker" },
  { key: "commissionReceived", label: "Commission Received" },
  { key: "paymentToAgents", label: "Payment to Agents" },
  { key: "followUpDate", label: "Follow Up Date", edit: "date" },
  { key: "action", label: "Action" },
  { key: "notes", label: "Notes" },
];

/** Optional fixed widths for on-screen report table (modal). */
export const PROGRESS_REPORT_COLUMN_CELL_CLASS = {
  tradeCategory: "min-w-[7.5rem] w-[7.5rem] max-w-[7.5rem]",
  tradeNumber: "min-w-[4.5rem] w-[4.5rem] max-w-[4.5rem]",
  address: "min-w-[11rem] w-[11rem] max-w-[11rem]",
  agent: "min-w-[10rem] w-[10rem] max-w-[10rem]",
  outsideBroker: "min-w-[9rem] w-[9rem] max-w-[9rem]",
  action: "min-w-[12rem] w-[12rem] max-w-[12rem]",
  notes: "min-w-[14rem] w-[14rem] max-w-[14rem]",
};

export function getProgressReportColumnCellClass(columnKey) {
  return PROGRESS_REPORT_COLUMN_CELL_CLASS[columnKey] || "";
}

/** Columns with filter dropdowns above the table (empty = all). */
export const PROGRESS_REPORT_FILTER_KEYS = PROGRESS_REPORT_COLUMNS.map(
  (c) => c.key,
);

export function getProgressReportFilterOptions(columnKey, rows) {
  const values = new Set();
  for (const row of rows || []) {
    const v = String(row[columnKey] ?? "").trim();
    values.add(v || "—");
  }
  const sorted = [...values].sort((a, b) => {
    if (a === "—") return 1;
    if (b === "—") return -1;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
  return [{ value: "", label: "All" }, ...sorted.map((v) => ({ value: v, label: v }))];
}

/** Filter value is a typed partial match (e.g. trade # or address contains query). */
export const PROGRESS_REPORT_TYPEABLE_FILTER_KEYS = new Set([
  "tradeNumber",
  "address",
]);

/** Columns that use a searchable react-select filter (dropdown + type-to-search). */
export const PROGRESS_REPORT_SEARCHABLE_FILTER_KEYS = new Set([
  "tradeNumber",
  "address",
]);

export function compareBaseTradeNumberDesc(a, b) {
  const na = parseInt(String(a).replace(/\D/g, ""), 10) || 0;
  const nb = parseInt(String(b).replace(/\D/g, ""), 10) || 0;
  if (nb !== na) return nb - na;
  return String(b).localeCompare(String(a));
}

/** Searchable address filter options from report rows (alphabetical). */
export function buildProgressReportAddressFilterOptions(rows) {
  const seen = new Set();
  const list = [];
  for (const row of rows || []) {
    const v = String(row.address ?? "").trim();
    if (!v || v === "—" || seen.has(v)) continue;
    seen.add(v);
    list.push({ value: v, label: v });
  }
  list.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );
  return list;
}

/** Unique trade # values for filter dropdown/datalist (highest first). */
export function getProgressReportTradeNumberFilterList(rows) {
  const seen = new Set();
  const list = [];
  for (const row of rows || []) {
    const v = String(row.tradeNumber ?? "").trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    list.push(v);
  }
  list.sort(compareBaseTradeNumberDesc);
  return list;
}

export function applyProgressReportFilters(rows, filters) {
  const active = Object.entries(filters || {}).filter(
    ([, v]) => v != null && String(v).trim() !== "",
  );
  if (!active.length) return rows || [];
  return (rows || []).filter((row) =>
    active.every(([key, selected]) => {
      const cell = String(row[key] ?? "").trim() || "—";
      const q = String(selected).trim();
      if (PROGRESS_REPORT_TYPEABLE_FILTER_KEYS.has(key)) {
        const ql = q.toLowerCase();
        if (key === "address") {
          const rowAddr = String(row.address ?? "")
            .trim()
            .toLowerCase();
          return rowAddr.includes(ql);
        }
        const rowTn = String(
          row.tradeNumber ?? row.baseTradeNumber ?? "",
        )
          .trim()
          .toLowerCase();
        return rowTn === ql || rowTn.includes(ql);
      }
      return cell === selected;
    }),
  );
}

export function getProgressReportColumnLabel(key) {
  const col = PROGRESS_REPORT_COLUMNS.find((c) => c.key === key);
  return col?.label || key;
}

/** Parse display date to yyyy-mm-dd for date inputs, or return "". */
export function progressReportDateForInput(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parts = raw.split(/[/-]/);
  if (parts.length === 3) {
    let y;
    let m;
    let d;
    if (parts[0].length === 4) {
      y = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      d = parseInt(parts[2], 10);
    } else {
      m = parseInt(parts[0], 10);
      d = parseInt(parts[1], 10);
      y = parseInt(parts[2], 10);
      if (y < 100) y += 2000;
    }
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${mo}-${day}`;
  }
  return "";
}

export function progressReportDateFromInput(isoValue) {
  if (!isoValue) return "";
  const [y, m, d] = isoValue.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return isoValue;
  return new Date(y, m - 1, d).toLocaleDateString("en-CA");
}
